import { Button, Form, Input, Select, Switch } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import FileDragAndDrop from "../../../../components/common/FileDragAndDrop";
import FileUploader from "../../../../components/common/FileUploader";
import TitleBar from "../../../../components/common/TitleBar";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import Loader from "../../../../components/ui/Loader";
import {
  useCreateSurveyTemplateMutation,
  useGetSurveyTemplateDetailsQuery,
  useUpdateSurveyTemplateMutation,
} from "../../../../features/organization/survey/surveyApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import SelectedQuestionType from "./_components/SelectedQuestionType";
import CheckboxGridIcon from "./_icons/CheckboxGridIcon";
import CheckboxIcon from "./_icons/CheckboxIcon";
import DateIcon from "./_icons/DateIcon";
import DropdownIcon from "./_icons/DropdownIcon";
import FileUploadIcon from "./_icons/FileUploadIcon";
import LinearScaleIcon from "./_icons/LinearScaleIcon";
import MultipleChoiceGridIcon from "./_icons/MultipleChoiceGridIcon";
import MultipleChoiceIcon from "./_icons/MultipleChoiceIcon";
import ParagraphIcon from "./_icons/ParagraphIcon";
import RatingIcon from "./_icons/RatingIcon";
import ShortAnswerIcon from "./_icons/ShortAnswerIcon";
import TimeIcon from "./_icons/TimeIcon";

const { Option, OptGroup } = Select;
export type TQOption = (typeof questionTypeOptions)[number][number]["value"];
const backLink = "/organizations/survey/templates";

export default function ManageTemplate() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const { open, handleOpen, handleClose } = useModal();
  const [createSurveyTemplate, { isLoading, isError, error, isSuccess }] =
    useCreateSurveyTemplateMutation();

  const [
    updateSurveyTemplate,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateSurveyTemplateMutation();
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetSurveyTemplateDetailsQuery(Number(id), {
    skip: !id,
  });
  useError(isError, error, "Template create fail");
  useError(isUpdateError, updateError, "Template update fail");
  useError(isDetailsError, detailsError, "Template details get fail");

  const onFinish = (values: any) => {
    // if (id) {
    //   updateSurveyTemplate({
    //     id: Number(id),
    //     body: values,
    //   });
    // } else {
    //   createSurveyTemplate(values);
    // }
    handleOpen({ type: "ADD", data: values });
  };

  useEffect(() => {
    if (Object.keys(detailsData?.data?.surveyTemplate || {}) && id) {
      const data = detailsData?.data?.surveyTemplate;

      form.setFieldsValue({
        cover_image: data?.cover_image,
        title: data?.title,
        description: data?.description,
        questions: data?.surveyQuestion,
      });
    }
  }, [form, id, detailsData?.data?.surveyTemplate]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Template created successfully");
      navigate(backLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Template updated successfully");
      navigate(backLink);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  return (
    <SidebarLayout title="Survey">
      <TitleBar
        title={`${id ? "Update" : "Create New"} Template`}
        backLink={backLink}
      />
      {open?.type === "ADD" && (
        <ConfirmModal
          title={id ? "Update Template?" : "Create Template?"}
          subTitle={
            id
              ? "Are you ready to update this template and make it visible?"
              : "Are you ready to publish this template and make it visible?"
          }
          width={450}
          handleClose={handleClose}
          open={open?.open}
          data={open?.data}
          okLoading={isUpdateLoading || isLoading}
          handleOk={(values) => {
            if (id) {
              updateSurveyTemplate({
                id: Number(id),
                body: values,
              });
            } else {
              createSurveyTemplate(values);
            }
          }}
          okText={id ? "Yes, Update" : "Yes, Create"}
        />
      )}
      {isDetailsLoading ? (
        <Loader />
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          size="large"
          className="custom-form"
          onFinish={onFinish}
        >
          <div className="space-y-6">
            {/* Cover Image */}
            <div className="p-4 bg-white rounded-lg">
              <Form.Item
                name="cover_image"
                label="Cover Image"
                className="mb-1"
              >
                <FileUploader
                  form={form}
                  name="cover_image"
                  fileSize={5}
                  accept=".png, .jpg, .jpeg, .gif, .svg, .webp"
                  component={(props) => <FileDragAndDrop {...props} />}
                  onChange={(url) => {
                    form.setFieldsValue({ cover_image: url });
                  }}
                />
              </Form.Item>
              <ul className="text-sm text-body-text list-disc pl-4 pt-1">
                <li>Supported Formats: JPG, JPEG, PNG, SVG, WEBP</li>
                <li>Maximum File Size: 5MB</li>
                <li>Recommended Dimensions: 800x800 pixels</li>
              </ul>
            </div>

            {/* Title and Description */}
            <div className="p-4 bg-white rounded-lg">
              <Form.Item
                name="title"
                label="Title"
                className="mb-4"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input size="large" placeholder="Enter title" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                className="mb-0"
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <TextArea rows={4} placeholder="Enter description..." />
              </Form.Item>
            </div>

            <Form.List
              name="questions"
              initialValue={[
                {
                  question_title: "",
                  question_type: "short_answer",
                  is_required: false,
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div className="p-4 bg-white rounded-lg" key={field.key}>
                      <Form.Item
                        name={[field.name, "question_order"]}
                        initialValue={index + 1}
                        className="sr-only"
                      >
                        <Input />
                      </Form.Item>

                      <div className="grid lg:grid-cols-4 md:grid-cols-3 gap-4">
                        <Form.Item
                          name={[field.name, "question_title"]}
                          label="Question Title"
                          className="lg:col-span-3 md:col-span-2 mb-0"
                          rules={[
                            {
                              required: true,
                              message: "Question title required",
                            },
                          ]}
                        >
                          <Input size="large" placeholder="Enter title" />
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "question_type"]}
                          label="Question Type"
                          className="mb-0"
                          initialValue="multiple_choice"
                        >
                          <Select
                            showSearch
                            placeholder="Select type"
                            optionFilterProp="children"
                            popupClassName="grouped-select-dropdown"
                            suffixIcon={<SelectDropdownLgIcon />}
                            onChange={() => {
                              const questions =
                                form.getFieldValue("questions") || [];
                              const updatedQuestions = [...questions];
                              if (updatedQuestions[field.name]) {
                                updatedQuestions[field.name][
                                  "question_choices"
                                ] = undefined;
                              }
                              form.setFieldsValue({
                                questions: updatedQuestions,
                              });
                            }}
                          >
                            {questionTypeOptions.map((group, idx) => (
                              <OptGroup key={idx} label="">
                                {group.map((opt) => (
                                  <Option
                                    key={opt.value}
                                    value={opt.value}
                                    label={opt.label}
                                  >
                                    <div className="flex items-center gap-2">
                                      {opt.icon}
                                      <span>{opt.label}</span>
                                    </div>
                                  </Option>
                                ))}
                              </OptGroup>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>

                      <SelectedQuestionType index={field.name} form={form} />

                      <div className="pt-4 mt-4 border-t border-light flex items-center gap-5 justify-end">
                        {fields.length > 1 && (
                          <>
                            <button
                              className="text-status-dark-text"
                              type="button"
                              onClick={() => remove(field.name)}
                            >
                              <DeleteIcon />
                            </button>
                            <span className="w-0 h-5 outline outline-1 outline-offset-[-0.50px] outline-light" />
                          </>
                        )}
                        <span className="text-[#3D4A5C] text-sm font-medium">
                          Required
                        </span>
                        <Form.Item
                          name={[field.name, "is_required"]}
                          valuePropName="checked"
                          className="mb-0"
                          initialValue={false}
                        >
                          <Switch />
                        </Form.Item>
                      </div>
                    </div>
                  ))}

                  <div className="mt-6">
                    <Button
                      htmlType="button"
                      icon={<AddIcon />}
                      size="middle"
                      className="!px-3 text-primary border-primary"
                      onClick={() =>
                        add({
                          question_title: "",
                          question_type: "multiple_choice",
                          question_choices: [""],
                          is_required: false,
                        })
                      }
                    >
                      Add New Field
                    </Button>
                  </div>
                </>
              )}
            </Form.List>
          </div>

          <div className="flex justify-between mt-6">
            <Link to={backLink}>
              <Button className="normal-dark-button modal-button" size="middle">
                Cancel
              </Button>
            </Link>

            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              loading={isLoading || isUpdateLoading}
            >
              {id ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      )}
    </SidebarLayout>
  );
}

const questionTypeOptions = [
  [
    { value: "short_answer", label: "Short Answer", icon: <ShortAnswerIcon /> },
    { value: "paragraph", label: "Paragraph", icon: <ParagraphIcon /> },
  ],
  [
    {
      value: "multiple_choice",
      label: "Multiple Choice",
      icon: <MultipleChoiceIcon />,
    },
    { value: "checkboxes", label: "Checkboxes", icon: <CheckboxIcon /> },
    { value: "dropdown", label: "Dropdown", icon: <DropdownIcon /> },
  ],
  [{ value: "file_upload", label: "File Upload", icon: <FileUploadIcon /> }],
  [
    { value: "linear_scale", label: "Linear Scale", icon: <LinearScaleIcon /> },
    { value: "rating", label: "Rating", icon: <RatingIcon /> },
    {
      value: "multiple_choice_grid",
      label: "Multiple Choice Grid",
      icon: <MultipleChoiceGridIcon />,
    },
    {
      value: "checkbox_grid",
      label: "Checkbox Grid",
      icon: <CheckboxGridIcon />,
    },
  ],
  [
    { value: "date", label: "Date", icon: <DateIcon /> },
    { value: "time", label: "Time", icon: <TimeIcon /> },
  ],
];
